import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Upload as UploadIcon, Image, Music, BookOpen, Eye, EyeOff, Sparkles, GripVertical, Wand2, Loader2, Maximize2, Minimize2, Zap, X } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import TranslationEditor from '../components/TranslationEditor';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// AI Image Generator Modal
const AIImageGenerator = ({ isOpen, onClose, onImageGenerated, initialPrompt = '' }) => {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [style, setStyle] = useState('cartoon');
  const [generating, setGenerating] = useState(false);
  const [budgetError, setBudgetError] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Beschrijf wat je wilt zien');
      return;
    }

    setGenerating(true);
    setBudgetError(false);
    try {
      const response = await axios.post(`${API}/image/generate`, {
        prompt: prompt,
        style: style
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onImageGenerated(imageUrl);
      toast.success('Illustratie gegenereerd! ✨');
      onClose();
    } catch (error) {
      console.error('Generation failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || '';
      const errorLower = errorMsg.toLowerCase();
      if (errorLower.includes('budget') || errorLower.includes('exceeded') || errorLower.includes('limit')) {
        setBudgetError(true);
        toast.error('AI budget is op. Upload een eigen afbeelding.');
      } else {
        toast.error('Genereren mislukt. Probeer een eigen afbeelding te uploaden.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Wand2 className="w-6 h-6 text-gold" />
            AI Illustratie Maken
          </DialogTitle>
          <DialogDescription>
            Beschrijf wat je wilt zien en de AI maakt een mooie illustratie
          </DialogDescription>
        </DialogHeader>

        {budgetError ? (
          <div className="py-6">
            <div className="text-center p-6 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mx-auto mb-4">
                <Wand2 className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-display text-xl text-amber-800 dark:text-amber-200 mb-2">
                AI Generatie Tijdelijk Niet Beschikbaar
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                Het AI budget is bereikt. Je kunt nog steeds prachtige verhalen maken door je eigen afbeeldingen te uploaden!
              </p>
              <Button
                onClick={onClose}
                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Eigen afbeelding uploaden
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="font-accent">Wat wil je zien?</Label>
                <Textarea
                  placeholder="Bijv: Vrolijke kinderen die samen spelen op een zonnige speelplaats met een regenboog"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-accent">Stijl</Label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cartoon">🎨 Cartoon (kleurrijk & vrolijk)</SelectItem>
                    <SelectItem value="watercolor">🖌️ Aquarel (zacht & dromerig)</SelectItem>
                    <SelectItem value="simple">✏️ Simpel (minimalistisch)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-muted/50 rounded-xl p-3">
                <p className="text-xs text-muted-foreground">
                  💡 <strong>Tips:</strong> Beschrijf de sfeer, kleuren en personages. 
                  Bijv: "Een klein meisje met rood haar knuffelt een teddybeer in een gezellige kamer met kaarslicht"
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Annuleren
              </Button>
              <Button 
                onClick={handleGenerate} 
                disabled={generating || !prompt.trim()}
                className="flex-1 bg-gold hover:bg-gold/90 text-night-blue font-bold"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Genereren...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Genereer Illustratie
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// AI Full Story Generator
const AIStoryGenerator = ({ onStoryGenerated }) => {
  const [theme, setTheme] = useState('');
  const [numPages, setNumPages] = useState(8);
  const [generating, setGenerating] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [progress, setProgress] = useState('');
  const { t, lang } = useLanguage();
  const { token } = useAuth();

  const handleGenerate = async () => {
    if (!theme.trim()) { toast.error('Voer een thema in'); return; }
    setGenerating(true);
    setProgress('Verhaal wordt geschreven...');
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/ai/generate-story`, {
        theme, num_pages: numPages, language: lang
      }, { headers });
      
      const storyData = res.data;
      
      // Ask user if they want illustrations too
      setProgress('Verhaal is klaar! Illustraties genereren...');
      setGeneratingImages(true);
      
      const pagesWithImages = [];
      for (let i = 0; i < storyData.pages.length; i++) {
        const page = storyData.pages[i];
        setProgress(`Illustratie ${i + 1} van ${storyData.pages.length}...`);
        
        try {
          const imgRes = await axios.post(`${API}/ai/generate-illustration`, {
            page_text: page.image_prompt || page.text,
            story_title: storyData.title,
            style: 'warm watercolor children\'s book illustration'
          }, { headers });
          
          // Upload base64 image to get a URL
          const uploadRes = await axios.post(`${API}/upload/base64-image`, {
            image_base64: imgRes.data.image_base64,
            filename: `ai_story_page_${i + 1}.png`
          }, { headers });
          
          pagesWithImages.push({
            page_number: i + 1,
            text: page.text,
            image_url: `${process.env.REACT_APP_BACKEND_URL}${uploadRes.data.url}`,
            image_size: 'large',
            audio_url: ''
          });
        } catch (imgError) {
          console.error(`Image gen failed for page ${i + 1}:`, imgError);
          pagesWithImages.push({
            page_number: i + 1,
            text: page.text,
            image_url: '',
            image_size: 'large',
            audio_url: ''
          });
        }
      }
      
      onStoryGenerated({
        title: storyData.title,
        description: storyData.description,
        age_group: storyData.age_group,
        pages: pagesWithImages
      });
      
      toast.success('Verhaal met illustraties gegenereerd!');
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Genereren mislukt. Probeer het opnieuw.');
    } finally {
      setGenerating(false);
      setGeneratingImages(false);
      setProgress('');
    }
  };

  const handleGenerateTextOnly = async () => {
    if (!theme.trim()) { toast.error('Voer een thema in'); return; }
    setGenerating(true);
    setProgress('Verhaal wordt geschreven...');
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/ai/generate-story`, {
        theme, num_pages: numPages, language: lang
      }, { headers });
      
      const storyData = res.data;
      onStoryGenerated({
        title: storyData.title,
        description: storyData.description,
        age_group: storyData.age_group,
        pages: storyData.pages.map((p, i) => ({
          page_number: i + 1,
          text: p.text,
          image_url: '',
          image_size: 'large',
          audio_url: ''
        }))
      });
      
      toast.success('Verhaal gegenereerd! Je kunt zelf illustraties toevoegen.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Genereren mislukt.');
    } finally {
      setGenerating(false);
      setProgress('');
    }
  };

  return (
    <Card className="border-2 border-purple-300/50 shadow-lg bg-gradient-to-br from-purple-50/50 to-gold/5 dark:from-purple-950/20 dark:to-gold/5" data-testid="ai-story-generator">
      <CardHeader>
        <CardTitle className="font-display text-2xl flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          AI Verhaal Generator
        </CardTitle>
        <p className="text-muted-foreground text-sm">Laat AI een compleet verhaal schrijven met illustraties</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="font-accent">Waar moet het verhaal over gaan?</Label>
          <Textarea
            data-testid="ai-theme-input"
            placeholder="Bijv: Een klein konijntje dat verdwaalt in een betoverd bos en nieuwe vrienden maakt"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            rows={2}
            className="resize-none"
            disabled={generating}
          />
        </div>
        <div className="space-y-2">
            <Label className="font-accent text-sm">Aantal pagina's</Label>
            <Select value={String(numPages)} onValueChange={(v) => setNumPages(Number(v))} disabled={generating}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 pagina's</SelectItem>
                <SelectItem value="8">8 pagina's</SelectItem>
                <SelectItem value="10">10 pagina's</SelectItem>
              </SelectContent>
            </Select>
          </div>

        {(generating || generatingImages) && (
          <div className="flex items-center gap-3 p-4 bg-purple-100/50 dark:bg-purple-900/20 rounded-xl" data-testid="ai-progress">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">{progress}</span>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            data-testid="ai-generate-full"
            onClick={handleGenerate}
            disabled={generating || !theme.trim()}
            className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Verhaal + Illustraties
          </Button>
          <Button
            type="button"
            data-testid="ai-generate-text"
            variant="outline"
            onClick={handleGenerateTextOnly}
            disabled={generating || !theme.trim()}
            className="rounded-full"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Alleen tekst
          </Button>
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Kosten: ~€0,05 (alleen tekst) of ~€0,50 (met illustraties) per verhaal
        </p>
      </CardContent>
    </Card>
  );
};

// Page Editor Component (Sortable)
const SortablePageEditor = ({ page, index, totalPages, onUpdate, onRemove, onImageUpload, onAudioUpload, id }) => {
  const [showPreview, setShowPreview] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onImageUpload(file, index); } finally { setUploading(false); }
  };

  const handleAudioFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { await onAudioUpload(file, index); } finally { setUploading(false); }
  };

  const handleAIImageGenerated = (imageUrl) => {
    onUpdate(index, 'image_url', imageUrl);
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <Card className={`border-2 overflow-hidden ${isDragging ? 'border-gold shadow-2xl' : 'border-border'}`}>
        {/* Page Header */}
        <CardHeader className="bg-gradient-to-r from-old-rose/20 to-gold/10 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                {...attributes}
                {...listeners}
                className="touch-none cursor-grab active:cursor-grabbing p-1.5 rounded-lg hover:bg-black/5 transition-colors"
                data-testid={`drag-handle-${index}`}
              >
                <GripVertical className="w-5 h-5 text-muted-foreground" />
              </button>
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="font-display text-xl text-gold">{page.page_number}</span>
              </div>
              <CardTitle className="font-display text-xl">
                Pagina {page.page_number}
              </CardTitle>
            </div>
            
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-full">
                {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                <span className="ml-1 hidden sm:inline">{showPreview ? 'Verberg' : 'Toon'}</span>
              </Button>
              {totalPages > 1 && (
                <Button type="button" data-testid={`remove-page-${index}`} variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-destructive hover:text-destructive rounded-full">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Image Section */}
            <div className="space-y-4">
              <Label className="font-accent text-base flex items-center gap-2">
                <Image className="w-5 h-5 text-gold" />
                Illustratie (optioneel)
              </Label>
              
              <div 
                className={`relative border-2 border-dashed rounded-2xl overflow-hidden transition-all ${page.image_url ? 'border-gold/50' : 'border-border hover:border-gold/50'}`}
                style={{ aspectRatio: '4/3' }}
              >
                {page.image_url ? (
                  <>
                    <img src={page.image_url} alt={`Pagina ${index + 1}`} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()} className="rounded-full bg-white text-night-blue hover:bg-white/90">
                        <UploadIcon className="w-4 h-4 mr-1" /> Upload
                      </Button>
                      <Button type="button" size="sm" onClick={() => setShowAIGenerator(true)} className="rounded-full bg-gold text-night-blue hover:bg-gold/90">
                        <Wand2 className="w-4 h-4 mr-1" /> AI
                      </Button>
                      <Button type="button" size="sm" variant="destructive" onClick={() => onUpdate(index, 'image_url', '')} className="rounded-full">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-muted/30">
                    {uploading ? (
                      <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
                    ) : (
                      <>
                        <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mb-3">
                          <Image className="w-8 h-8 text-gold" />
                        </div>
                        <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()} className="rounded-full bg-gold hover:bg-gold/90 text-night-blue mb-3">
                          <UploadIcon className="w-4 h-4 mr-1" /> Upload afbeelding
                        </Button>
                        <p className="text-xs text-muted-foreground/60">of plak een URL hieronder</p>
                      </>
                    )}
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
              </div>

              <div className="flex gap-2">
                <Input data-testid={`page-image-${index}`} placeholder="Of plak hier een afbeelding URL..." value={page.image_url} onChange={(e) => onUpdate(index, 'image_url', e.target.value)} className="flex-1" />
              </div>

              {page.image_url && (
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Maximize2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Afbeeldingsgrootte:</span>
                  <div className="flex gap-1 ml-auto">
                    {['small', 'medium', 'large'].map(size => (
                      <Button key={size} type="button" size="sm" variant={(size === 'large' && !page.image_size) || page.image_size === size ? 'default' : 'outline'} onClick={() => onUpdate(index, 'image_size', size)} className="h-7 px-2 text-xs">
                        {size === 'small' ? 'Klein' : size === 'medium' ? 'Middel' : 'Groot'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Audio Section */}
              <div className="pt-4 border-t border-border">
                <Label className="font-accent text-sm flex items-center gap-2 mb-2">
                  <Music className="w-4 h-4 text-gold" /> Eigen audio (optioneel)
                </Label>
                <p className="text-xs text-muted-foreground mb-2">Upload je eigen voorleesaudio (optioneel)</p>
                <div className="flex gap-2">
                  <Input data-testid={`page-audio-${index}`} placeholder="Audio URL of upload..." value={page.audio_url} onChange={(e) => onUpdate(index, 'audio_url', e.target.value)} className="flex-1" />
                  <label className="cursor-pointer">
                    <input ref={audioInputRef} type="file" accept="audio/*" className="hidden" onChange={handleAudioFile} />
                    <Button type="button" variant="outline" size="icon" asChild><span><UploadIcon className="w-4 h-4" /></span></Button>
                  </label>
                </div>
                {page.audio_url && (
                  <div className="mt-2 p-2 bg-muted/30 rounded-lg flex items-center gap-2">
                    <Music className="w-4 h-4 text-gold" />
                    <span className="text-xs text-muted-foreground truncate flex-1">Audio toegevoegd</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => onUpdate(index, 'audio_url', '')} className="h-6 w-6 p-0 text-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Text Section */}
            <div className="space-y-4">
              <Label className="font-accent text-base flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" /> Verhaaltekst voor deze pagina *
              </Label>
              <Textarea data-testid={`page-text-${index}`} placeholder="Er was eens, in een land heel ver weg..." value={page.text} onChange={(e) => onUpdate(index, 'text', e.target.value)} required rows={8} className="resize-none text-base leading-relaxed" />
              <p className="text-xs text-muted-foreground">Tip: Schrijf zinnen die goed hardop voorgelezen kunnen worden.</p>

              {showPreview && page.text && (
                <div className="mt-4 p-4 bg-soft-white dark:bg-deep-blue rounded-xl border border-border">
                  <p className="text-xs text-muted-foreground mb-2 font-accent">Preview:</p>
                  <div className="flex gap-3">
                    {page.image_url && <img src={page.image_url} alt="Preview" className="w-20 h-16 object-cover rounded-lg flex-shrink-0" />}
                    <p className="text-sm leading-relaxed font-body line-clamp-4">{page.text}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <AIImageGenerator isOpen={showAIGenerator} onClose={() => setShowAIGenerator(false)} onImageGenerated={handleAIImageGenerated} initialPrompt={page.text ? page.text.substring(0, 200) : ''} />
    </div>
  );
};

// Live Preview Modal
const LivePreview = ({ story, isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Eye className="w-6 h-6 text-gold" />
            Live Preview
          </DialogTitle>
          <DialogDescription>Zo ziet je verhaal eruit voor de lezer</DialogDescription>
        </DialogHeader>
        <div className="space-y-0">
          {/* Cover */}
          <div className="relative aspect-[3/4] max-h-[300px] w-full rounded-2xl overflow-hidden mb-6">
            {story.cover_image_url ? (
              <img src={story.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-old-rose/60 to-gold/40 flex items-center justify-center">
                <BookOpen className="w-16 h-16 text-white/40" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-night-blue/80 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="font-display text-3xl text-cream drop-shadow-lg">{story.title || 'Titel...'}</h2>
              <p className="text-cream/80 text-sm mt-1 font-accent">{story.description || 'Beschrijving...'}</p>
            </div>
          </div>

          {/* Pages */}
          {story.pages.map((page, i) => (
            <div key={i} className="border-t border-border pt-6 pb-4">
              <p className="text-xs text-muted-foreground font-accent mb-3">Pagina {i + 1}</p>
              {page.image_url && (
                <div className="mb-4 rounded-xl overflow-hidden" style={{ maxHeight: page.image_size === 'small' ? '120px' : page.image_size === 'medium' ? '200px' : '280px' }}>
                  <img src={page.image_url} alt={`Pagina ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              )}
              <p className="text-base leading-relaxed font-body text-foreground whitespace-pre-wrap">
                {page.text || <span className="text-muted-foreground italic">Nog geen tekst...</span>}
              </p>
            </div>
          ))}

          {/* End page */}
          <div className="text-center py-8 border-t border-border">
            <p className="font-display text-2xl text-gold">Einde</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Upload = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { user } = useAuth();
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [story, setStory] = useState({
    title: '',
    description: '',
    cover_image_url: '',
    pages: [{ page_number: 1, text: '', image_url: '', image_size: 'large', audio_url: '' }],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  useEffect(() => {
    checkProfile();
  }, []);

  useEffect(() => {
    if (editId && hasProfile) {
      loadStoryForEdit(editId);
    }
  }, [editId, hasProfile]);

  const checkProfile = async () => {
    try {
      const response = await axios.get(`${API}/grandmas/me`);
      setHasProfile(!!response.data);
    } catch (error) {
      setHasProfile(false);
    } finally {
      setLoading(false);
    }
  };

  const loadStoryForEdit = async (storyId) => {
    try {
      const response = await axios.get(`${API}/stories/${storyId}?track_read=false`);
      const s = response.data;
      setStory({
        title: s.title,
        description: s.description,
        cover_image_url: s.cover_image_url || '',
        pages: s.pages.map(p => ({
          page_number: p.page_number,
          text: p.text,
          image_url: p.image_url || '',
          image_size: p.image_size || 'large',
          audio_url: p.audio_url || '',
        })),
      });
      setIsEditMode(true);
    } catch (error) {
      toast.error('Kon verhaal niet laden');
      navigate('/profile');
    }
  };

  const handleAddPage = () => {
    setStory({
      ...story,
      pages: [
        ...story.pages,
        { page_number: story.pages.length + 1, text: '', image_url: '', image_size: 'large', audio_url: '' },
      ],
    });
  };

  const handleRemovePage = (index) => {
    if (story.pages.length <= 1) return;
    const newPages = story.pages.filter((_, i) => i !== index).map((page, i) => ({
      ...page,
      page_number: i + 1,
    }));
    setStory({ ...story, pages: newPages });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = story.pages.findIndex((_, i) => `page-${i}` === active.id);
    const newIndex = story.pages.findIndex((_, i) => `page-${i}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newPages = arrayMove(story.pages, oldIndex, newIndex).map((page, i) => ({
      ...page,
      page_number: i + 1,
    }));
    setStory({ ...story, pages: newPages });
  };

  const handlePageChange = (index, field, value) => {
    const newPages = [...story.pages];
    newPages[index] = { ...newPages[index], [field]: value };
    setStory({ ...story, pages: newPages });
  };

  const handleImageUpload = async (file, index) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (index === -1) {
        // Cover image
        setStory({ ...story, cover_image_url: url });
      } else {
        handlePageChange(index, 'image_url', url);
      }
      toast.success('Afbeelding geüpload! 🖼️');
    } catch (error) {
      toast.error('Uploaden mislukt');
    }
  };

  const handleAudioUpload = async (file, pageIndex) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handlePageChange(pageIndex, 'audio_url', `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`);
      toast.success('Audio geüpload! 🎵');
    } catch (error) {
      toast.error('Uploaden mislukt');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!story.title || !story.description || story.pages.some(p => !p.text)) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    setSubmitting(true);

    try {
      if (isEditMode && editId) {
        await axios.put(`${API}/stories/${editId}`, story);
        toast.success('Verhaal bijgewerkt! Wordt opnieuw beoordeeld.');
      } else {
        await axios.post(`${API}/stories`, story);
        toast.success('Verhaal ingediend voor goedkeuring!');
      }
      navigate('/profile');
    } catch (error) {
      const message = error.response?.data?.detail || 'Er ging iets mis';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="upload-loading">
        <div className="w-8 h-8 border-4 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="upload-no-profile">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8">
            <div className="text-6xl mb-4">👵</div>
            <h2 className="font-display text-2xl text-foreground mb-2">
              Maak eerst je Oma-profiel
            </h2>
            <p className="text-muted-foreground font-accent mb-6">
              Voordat je verhalen kunt uploaden, moet je een profiel aanmaken.
            </p>
            <Button
              data-testid="go-to-profile"
              onClick={() => navigate('/profile')}
              className="rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold"
            >
              Profiel aanmaken
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="upload-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-2">
              {isEditMode ? 'Verhaal bewerken' : 'Nieuw verhaal maken'}
            </h1>
            <p className="text-muted-foreground font-accent text-lg">
              {isEditMode ? 'Pas je verhaal aan en sla het op' : 'Maak een prachtig verhaal voor de kinderen'}
            </p>
          </div>

          {/* AI Story Generator */}
          {!isEditMode && (
            <AIStoryGenerator 
              onStoryGenerated={(data) => {
                setStory({
                  title: data.title,
                  description: data.description,
                  cover_image_url: data.pages[0]?.image_url || '',
                  pages: data.pages
                });
              }}
            />
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Story details */}
            <Card className="border-2 border-gold/30 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-gold/10 to-old-rose/10">
                <CardTitle className="font-display text-2xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-gold" />
                  </div>
                  Verhaal informatie
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left: Basic info */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title" className="font-accent text-base">Titel van je verhaal *</Label>
                      <Input
                        id="title"
                        data-testid="story-title"
                        placeholder="Bijv: Het Kleine Sterretje"
                        value={story.title}
                        onChange={(e) => setStory({ ...story, title: e.target.value })}
                        required
                        className="text-lg h-12"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="font-accent text-base">Korte beschrijving *</Label>
                      <Textarea
                        id="description"
                        data-testid="story-description"
                        placeholder="Waar gaat dit verhaal over? (Dit zien de kinderen bij de cover)"
                        value={story.description}
                        onChange={(e) => setStory({ ...story, description: e.target.value })}
                        required
                        rows={4}
                      />
                    </div>
                  </div>

                  {/* Right: Cover image */}
                  <div className="space-y-2">
                    <Label className="font-accent text-base">Cover afbeelding</Label>
                    <div 
                      className={`relative border-2 border-dashed rounded-2xl overflow-hidden cursor-pointer transition-all ${
                        story.cover_image_url ? 'border-gold/50' : 'border-border hover:border-gold/50'
                      }`}
                      style={{ aspectRatio: '3/4', maxHeight: '250px' }}
                      onClick={() => document.getElementById('cover-upload')?.click()}
                    >
                      {story.cover_image_url ? (
                        <>
                          <img 
                            src={story.cover_image_url} 
                            alt="Cover" 
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              type="button"
                              size="sm"
                              className="rounded-full bg-white text-night-blue"
                            >
                              <UploadIcon className="w-4 h-4 mr-1" />
                              Wijzigen
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-muted/30">
                          <Image className="w-12 h-12 text-muted-foreground/50 mb-2" />
                          <p className="text-sm text-muted-foreground">Klik om cover te uploaden</p>
                        </div>
                      )}
                    </div>
                    <input
                      id="cover-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(file, -1);
                      }}
                    />
                    <Input
                      data-testid="story-cover-url"
                      placeholder="Of plak een afbeelding URL..."
                      value={story.cover_image_url}
                      onChange={(e) => setStory({ ...story, cover_image_url: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pages Section Header */}
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-foreground">
                Pagina's ({story.pages.length})
              </h2>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="live-preview-btn"
                  variant="outline"
                  onClick={() => setShowLivePreview(true)}
                  className="rounded-full"
                  disabled={!story.title && story.pages.every(p => !p.text)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Live Preview
                </Button>
                <Button
                  type="button"
                  data-testid="add-page-top"
                  variant="outline"
                  onClick={handleAddPage}
                  className="rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Pagina toevoegen
                </Button>
              </div>
            </div>

            {/* Pages with Drag & Drop */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={story.pages.map((_, i) => `page-${i}`)} strategy={verticalListSortingStrategy}>
                <div className="space-y-6">
                  {story.pages.map((page, index) => (
                    <SortablePageEditor
                      key={`page-${index}`}
                      id={`page-${index}`}
                      page={page}
                      index={index}
                      totalPages={story.pages.length}
                      onUpdate={handlePageChange}
                      onRemove={handleRemovePage}
                      onImageUpload={handleImageUpload}
                      onAudioUpload={handleAudioUpload}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Add page button */}
            <Button
              type="button"
              data-testid="add-page"
              variant="outline"
              onClick={handleAddPage}
              className="w-full rounded-2xl border-dashed border-2 py-8 text-lg hover:bg-gold/5 hover:border-gold/50 transition-all"
            >
              <Plus className="w-6 h-6 mr-2" />
              Nog een pagina toevoegen
            </Button>

            {/* Translation Editor - only in edit mode for admins */}
            {isEditMode && editId && user?.is_admin && (
              <TranslationEditor storyId={editId} originalStory={story} />
            )}

            {/* Live Preview Modal */}
            <LivePreview story={story} isOpen={showLivePreview} onClose={() => setShowLivePreview(false)} />

            {/* Submit */}
            <div className="sticky bottom-20 md:bottom-4 bg-background/95 backdrop-blur-sm p-4 -mx-4 rounded-t-2xl border-t border-border">
              <Button
                type="submit"
                data-testid="submit-story"
                disabled={submitting}
                className="w-full rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold py-6 text-lg shadow-lg"
              >
                {submitting ? (
                  <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    {isEditMode ? 'Wijzigingen opslaan' : 'Verhaal indienen voor goedkeuring'}
                  </>
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground font-accent mt-2">
                {isEditMode 
                  ? 'Na het opslaan wordt je verhaal opnieuw beoordeeld'
                  : 'Je verhaal wordt gecontroleerd voordat het zichtbaar wordt voor de kinderen'}
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Upload;
